/*
 * r16sd (2026-10-27) - the Settings Doctor in V3 (design docs/SETTINGS-DOCTOR-DESIGN.md 5.3, server API
 * D:/NecroWork/merge-r16sd/DOCTOR-API.md). Every part is drawn only from what the server sends: a server without the doctor
 * (r16pd, or -Dnecro.server.doctor=false) sends no `doctor` / `credits` on /uiapi/me and no `holds` / `attention` on /uiapi/slots
 * rows, and then nothing below is requested or drawn (the r16pd pages exactly).
 *
 * ONE master switch plus one switch per part, all ON by default (owner rule: features ship on):
 *
 *   master       necro.ui.r16sd                    REACT_APP_NECRO_UI_R16SD
 *   banner       necro.ui.r16sd.banner             REACT_APP_NECRO_UI_R16SD_BANNER          the slot banner (row line, card strip)
 *   slotsList    necro.ui.r16sd.slotsList          REACT_APP_NECRO_UI_R16SD_SLOTSLIST       the slots page dot, "Needs attention" chip + filter
 *   settingsHeader necro.ui.r16sd.settingsHeader  REACT_APP_NECRO_UI_R16SD_SETTINGSHEADER  the slot settings page header banners
 *   drawer       necro.ui.r16sd.drawer             REACT_APP_NECRO_UI_R16SD_DRAWER          "Check this slot": the doctor drawer
 *   saveFindings necro.ui.r16sd.saveFindings       REACT_APP_NECRO_UI_R16SD_SAVEFINDINGS    the findings of a save, with their fixes
 *   blockedToast necro.ui.r16sd.blockedToast       REACT_APP_NECRO_UI_R16SD_BLOCKEDTOAST    SD36: the settings a save did not write
 *   accountBanner necro.ui.r16sd.accountBanner     REACT_APP_NECRO_UI_R16SD_ACCOUNTBANNER   the account banner at the top of every page
 *   credits      necro.ui.r16sd.credits            REACT_APP_NECRO_UI_R16SD_CREDITS         post / target credits in the header
 *   followBackNote necro.ui.r16sd.followBackNote   REACT_APP_NECRO_UI_R16SD_FOLLOWBACKNOTE  SD22 on the Follow-back page
 *   dashboardTile necro.ui.r16sd.dashboardTile     REACT_APP_NECRO_UI_R16SD_DASHBOARDTILE   the "Needs attention" tile on the dashboard
 *   fixApply     necro.ui.r16sd.fixApply           REACT_APP_NECRO_UI_R16SD_FIXAPPLY        stage 5: a "set" fix (and "Use my account email")
 *                                                                                           as a one-click button (POST .../doctor/fix); off =
 *                                                                                           a link that opens the setting, as before
 *   dismiss      necro.ui.r16sd.dismiss            REACT_APP_NECRO_UI_R16SD_DISMISS         stage 5: "Don't show again" on a finding
 *
 *   build time: REACT_APP_NECRO_UI_R16SD=off (also 0 / false / no), or one part's variable;
 *   run time:   localStorage['necro.ui.r16sd'] = 'off' (or 'necro.ui.r16sd.<part>' = 'off') in one browser, then reload; 'on' there
 *               overrides a build-time off. The master OFF turns every part off.
 */

const OFF_WORDS = new Set(['off', '0', 'false', 'no']);
const ON_WORDS = new Set(['on', '1', 'true', 'yes']);
const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

export const DOCTOR_TAG = '[DOCTOR-UI]';
export const R16SD_TAG = '[R16SD-UI]';

export const DOCTOR_PARTS = Object.freeze([
  'banner', 'slotsList', 'settingsHeader', 'drawer', 'saveFindings', 'blockedToast', 'accountBanner', 'credits', 'followBackNote',
  'dashboardTile', 'fixApply', 'dismiss',
]);

/* Spelled out one by one: CRA inlines only a literal process.env.REACT_APP_X reference. */
function envFor(part) {
  switch (part) {
    case 'master': return process.env.REACT_APP_NECRO_UI_R16SD;
    case 'banner': return process.env.REACT_APP_NECRO_UI_R16SD_BANNER;
    case 'slotsList': return process.env.REACT_APP_NECRO_UI_R16SD_SLOTSLIST;
    case 'settingsHeader': return process.env.REACT_APP_NECRO_UI_R16SD_SETTINGSHEADER;
    case 'drawer': return process.env.REACT_APP_NECRO_UI_R16SD_DRAWER;
    case 'saveFindings': return process.env.REACT_APP_NECRO_UI_R16SD_SAVEFINDINGS;
    case 'blockedToast': return process.env.REACT_APP_NECRO_UI_R16SD_BLOCKEDTOAST;
    case 'accountBanner': return process.env.REACT_APP_NECRO_UI_R16SD_ACCOUNTBANNER;
    case 'credits': return process.env.REACT_APP_NECRO_UI_R16SD_CREDITS;
    case 'followBackNote': return process.env.REACT_APP_NECRO_UI_R16SD_FOLLOWBACKNOTE;
    case 'dashboardTile': return process.env.REACT_APP_NECRO_UI_R16SD_DASHBOARDTILE;
    case 'fixApply': return process.env.REACT_APP_NECRO_UI_R16SD_FIXAPPLY;
    case 'dismiss': return process.env.REACT_APP_NECRO_UI_R16SD_DISMISS;
    default: return undefined;
  }
}

const storageKey = (part) => (part === 'master' ? 'necro.ui.r16sd' : `necro.ui.r16sd.${part}`);
const envName = (part) => (part === 'master' ? 'REACT_APP_NECRO_UI_R16SD' : `REACT_APP_NECRO_UI_R16SD_${part.toUpperCase()}`);

function readStorage(key) {
  // Private windows, blocked site data and thumbnail renderers throw on the accessor itself.
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

/** Pure: one switch from its build value and its browser value (the r14Switch rule). */
export function resolveDoctorSwitch(part, envValue, storageValue) {
  const key = storageKey(part);
  const s = norm(storageValue);
  if (OFF_WORDS.has(s)) return { enabled: false, source: `localStorage ${key}=${s}` };
  if (ON_WORDS.has(s)) return { enabled: true, source: `localStorage ${key}=${s}` };
  const e = norm(envValue);
  if (OFF_WORDS.has(e)) return { enabled: false, source: `build ${envName(part)}=${e}` };
  return { enabled: true, source: e ? `build ${envName(part)}=${e}` : 'default' };
}

let logged = false;

/** Every switch as the page sees it now: { master, parts: {part: bool}, sources }. Logs the decision once per page load. */
export function doctorSwitches() {
  const master = resolveDoctorSwitch('master', envFor('master'), readStorage(storageKey('master')));
  const parts = {};
  const off = [];
  DOCTOR_PARTS.forEach((p) => {
    const r = resolveDoctorSwitch(p, envFor(p), readStorage(storageKey(p)));
    parts[p] = master.enabled && r.enabled;
    if (!r.enabled) off.push(`${p} (${r.source})`);
  });
  if (!logged) {
    logged = true;
    // eslint-disable-next-line no-console
    console.info(`${R16SD_TAG} Settings Doctor UI ${master.enabled ? 'ON' : 'OFF'} (${master.source}); parts off: ${off.length ? off.join(', ') : 'none'}`);
  }
  return { master: master.enabled, parts };
}

/** true when the master and this part are both on. */
export function doctorUiOn(part) {
  const s = doctorSwitches();
  return !!(s.master && s.parts[part]);
}

/** Tests only: forget that the decision was logged. */
export function resetDoctorSwitchLog() {
  logged = false;
}
